/**
 * Reports — the builder (#239). `/reports/new` and `/reports/:id/edit`.
 * BD.Admin only: the page self-redirects without `reports.manage`, and
 * POST/PUT/DELETE /reports are admin-only on the server, which is the
 * boundary.
 *
 * Actions:
 *   draft (or new)  Save draft · Publish
 *   published       Save · Unpublish   (only the owner may unpublish; anyone
 *                                       else gets the server's 403 message)
 *   existing        Delete (confirmed)
 *
 * A save the server rejects comes back as a 422 whose entries carry
 * `loc: ["body","spec","widgets",i,...]`; those are shown on widget i, the
 * rest above the form.
 *
 * Audience: a report for brokers (External / All) that uses fields hidden
 * from them never reaches them. The builder lists those fields — the
 * server's `audience_issues` from the last load / save while the draft is
 * unchanged since, merged with a live check of the draft — and publishing
 * (or saving while published) with any asks first: publish anyway, set the
 * audience to Internal, or cancel. Never blocked.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Modal,
  Popconfirm,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router";

import { WigAntLayout } from "@warwick-investment-group/wig-react-library";

import SignIn from "../signIn";
import { AppContext } from "../../AppContextProvider";
import usePermissions from "../../hooks/usePermissions";
import useReportsDataSource from "../../hooks/useReportsDataSource";
import reportsService from "../../services/reportsService";
import { apiErrorText } from "../../utils/apiError";
import {
  WigReportBuilder,
  WigReportsDataSourceProvider,
  buildSpec,
  describeIssue,
  splitSaveIssues,
  DEFAULT_AUDIENCE,
  audienceIssueFieldCount,
  draftAudienceIssues,
  mergeAudienceIssues,
  normalizeAudience,
  normalizeCategory,
  reachesBrokers,
  usedCategories,
} from "@warwick-investment-group/wig-react-library/reports";
import { reportLayoutProps } from "./layout";

const { Title, Paragraph } = Typography;

const EMPTY_DRAFT = {
  name: "",
  description: "",
  title: "",
  widgets: [],
  parameters: [],
  about: "",
  audience: DEFAULT_AUDIENCE,
  category: "",
};

const draftFromReport = (r) => ({
  name: r?.name || "",
  description: r?.description || "",
  title: r?.spec?.title || "",
  widgets: Array.isArray(r?.spec?.widgets) ? r.spec.widgets : [],
  parameters: Array.isArray(r?.spec?.parameters) ? r.spec.parameters : [],
  about: r?.spec?.about || "",
  audience: normalizeAudience(r?.audience),
  category: r?.category || "",
});

const asArray = (v) => (Array.isArray(v) ? v : []);

const ReportBuild = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const { theme } = useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("reports.manage");
  const { dataSource, call } = useReportsDataSource();

  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [report, setReport] = useState(null); // saved summary: id, status, owner
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [topIssues, setTopIssues] = useState([]);
  const [widgetIssues, setWidgetIssues] = useState({});
  const [categories, setCategories] = useState([]);
  // The server's audience_issues, and the draft they describe: they are
  // shown only while the draft is that same object (unedited since).
  const [serverAudience, setServerAudience] = useState({ issues: [], draft: null });
  // The publish / save awaiting the "brokers won't see it" decision.
  const [confirmStatus, setConfirmStatus] = useState(null);

  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const [cat, rep, list] = await Promise.all([
        call(reportsService.getCatalog),
        isNew ? Promise.resolve(null) : call(reportsService.getReport, { id }),
        // Category suggestions only: a failure just means none.
        Promise.resolve()
          .then(() => call(reportsService.listReports))
          .catch(() => null),
      ]);
      setCatalog(cat?.data?.data || null);
      setCategories(usedCategories(list?.data?.data));
      const r = rep?.data?.data || null;
      setReport(r);
      const next = r ? draftFromReport(r) : EMPTY_DRAFT;
      setDraft(next);
      setServerAudience({ issues: asArray(r?.audience_issues), draft: next });
    } catch (err) {
      setErrorMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [call, id, isNew]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const clearIssues = () => {
    setErrorMsg(null);
    setTopIssues([]);
    setWidgetIssues({});
  };

  // `widgets` is the list as it was SENT: the 422 indexes into that, and
  // keying by id keeps each issue on its widget through later reorders.
  const handleError = (err, action, widgets = []) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 422 && Array.isArray(detail)) {
      const { byWidget, other } = splitSaveIssues(detail);
      const byId = {};
      Object.entries(byWidget).forEach(([i, list]) => {
        const wid = widgets[Number(i)]?.id;
        if (wid != null) byId[wid] = list;
        else other.push(...list);
      });
      setWidgetIssues(byId);
      setTopIssues(other);
      setErrorMsg(`Couldn't ${action}. Fix the problems marked below.`);
    } else {
      setErrorMsg(`Couldn't ${action}. ${apiErrorText(err)}`);
    }
  };

  const published = report?.status === "published";

  // Broker-hidden fields the draft uses, when its audience reaches brokers:
  // the server's (if the draft is unchanged since it said so) plus the live
  // check. Server entries without a field are informational notes.
  const clientAudienceIssues = useMemo(
    () => draftAudienceIssues(draft, catalog),
    [draft, catalog]
  );
  const forBrokers = reachesBrokers(draft.audience);
  const serverFresh = serverAudience.draft === draft;
  const audienceIssues = forBrokers
    ? mergeAudienceIssues(
        serverFresh ? serverAudience.issues.filter((i) => i?.field) : [],
        clientAudienceIssues
      )
    : [];
  const audienceNotes = forBrokers
    ? serverAudience.issues.filter((i) => i && !i.field)
    : [];
  const hiddenCount = audienceIssueFieldCount(audienceIssues);

  /** Create or update; `status` set only when the action changes it. */
  const save = async (status, current = draft) => {
    const name = current.name.trim();
    if (!name) {
      setErrorMsg("Give the report a name before saving.");
      return;
    }
    const body = {
      name,
      description: current.description?.trim() || null,
      audience: normalizeAudience(current.audience),
      category: normalizeCategory(current.category),
      spec: buildSpec(current.title?.trim() || name, current.widgets, current.parameters, current.about),
    };
    if (status) body.status = status;
    const action = status === "published" ? "publish the report" : "save the report";
    try {
      setBusy(true);
      clearIssues();
      if (isNew) {
        const resp = await call(reportsService.createReport, {
          body: { ...body, status: status || "draft" },
        });
        const created = resp?.data?.data;
        message.success(status === "published" ? "Report published" : "Draft saved");
        navigate(
          status === "published" ? `/reports/${created.id}` : `/reports/${created.id}/edit`,
          { replace: true }
        );
        return;
      }
      const resp = await call(reportsService.updateReport, { id, body });
      const updated = resp?.data?.data;
      if (updated) {
        setReport((r) => ({ ...r, ...updated }));
        setServerAudience({ issues: asArray(updated.audience_issues), draft: current });
      }
      message.success(status === "published" ? "Report published" : "Saved");
      if (status === "published") navigate(`/reports/${id}`);
    } catch (err) {
      handleError(err, action, current.widgets);
    } finally {
      setBusy(false);
    }
  };

  /** Back to draft — status only, so unsaved edits stay on screen. */
  const unpublish = async () => {
    try {
      setBusy(true);
      clearIssues();
      const resp = await call(reportsService.updateReport, {
        id,
        body: { status: "draft" },
      });
      const updated = resp?.data?.data;
      setReport((r) => ({ ...r, ...(updated || {}), status: "draft" }));
      message.success("Report unpublished");
    } catch (err) {
      handleError(err, "unpublish the report");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      setBusy(true);
      clearIssues();
      await call(reportsService.deleteReport, { id });
      message.success("Report deleted");
      navigate("/reports", { replace: true });
    } catch (err) {
      handleError(err, "delete the report");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Publish, or save while published: if brokers are meant to see the report
   * but it uses fields hidden from them, ask first. Never blocked.
   */
  const requestSave = (status) => {
    const publishing = status === "published" || (published && !status);
    if (publishing && hiddenCount > 0 && draft.name.trim()) {
      setConfirmStatus({ status });
      return;
    }
    save(status);
  };

  const confirmAnyway = () => {
    const { status } = confirmStatus;
    setConfirmStatus(null);
    save(status);
  };

  const confirmInternal = () => {
    const { status } = confirmStatus;
    setConfirmStatus(null);
    const next = { ...draft, audience: "internal" };
    setDraft(next);
    save(status, next);
  };

  if (!allowed) return null;

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row>
          <WigAntLayout
            {...reportLayoutProps(theme === "dark")}
            content={
              <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Space align="center">
                    <Button
                      type="text"
                      aria-label={isNew ? "Back to reports" : "Back to report"}
                      icon={<ArrowLeftOutlined />}
                      onClick={() => navigate(isNew ? "/reports" : `/reports/${id}`)}
                    />
                    <Title level={3} style={{ margin: 0 }}>
                      {isNew ? "New report" : "Edit report"}
                    </Title>
                    {report && (
                      <Tag color={published ? "green" : "gold"}>
                        {published ? "Published" : "Draft"}
                      </Tag>
                    )}
                  </Space>
                  <Space wrap>
                    {!isNew && (
                      <Popconfirm
                        title="Delete this report?"
                        description="This cannot be undone."
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={remove}
                      >
                        <Button danger icon={<DeleteOutlined />} disabled={busy || loading}>
                          Delete
                        </Button>
                      </Popconfirm>
                    )}
                    {published ? (
                      <>
                        <Button onClick={unpublish} disabled={busy || loading}>
                          Unpublish
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => requestSave()}
                          loading={busy}
                          disabled={loading}
                        >
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={() => save()} loading={busy} disabled={loading}>
                          Save draft
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => requestSave("published")}
                          loading={busy}
                          disabled={loading}
                        >
                          Publish
                        </Button>
                      </>
                    )}
                  </Space>
                </Row>
                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    title={errorMsg}
                    description={
                      topIssues.length ? (
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {topIssues.map((i, k) => (
                            <li key={k}>{describeIssue(i)}</li>
                          ))}
                        </ul>
                      ) : null
                    }
                  />
                )}
                {loading ? (
                  <Skeleton active />
                ) : (
                  catalog && (
                    <WigReportsDataSourceProvider value={dataSource}>
                      <WigReportBuilder
                        value={draft}
                        onChange={setDraft}
                        catalog={catalog}
                        widgetIssues={widgetIssues}
                        categories={categories}
                        audienceIssues={audienceIssues}
                        audienceNotes={audienceNotes}
                      />
                    </WigReportsDataSourceProvider>
                  )
                )}
              </Space>
            }
          />
        </Row>
        <Modal
          open={Boolean(confirmStatus)}
          title="Brokers won't be able to see this report"
          onCancel={() => setConfirmStatus(null)}
          footer={[
            <Button key="cancel" onClick={() => setConfirmStatus(null)}>
              Cancel
            </Button>,
            <Button key="internal" onClick={confirmInternal}>
              Set to Internal
            </Button>,
            <Button key="publish" type="primary" onClick={confirmAnyway}>
              Publish anyway
            </Button>,
          ]}
        >
          <Paragraph style={{ margin: 0 }}>
            Brokers won&apos;t be able to see this report because it uses{" "}
            {hiddenCount} {hiddenCount === 1 ? "field" : "fields"} hidden from
            them. Publish anyway, or set the audience to Internal?
          </Paragraph>
        </Modal>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default ReportBuild;
